import { useMemo } from 'react';
import {
  CheckCircle as ValidationIcon,
  Settings as SettingsIcon,
  AutoFixHigh as QuickFillIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import type { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import type { AdvancedSettings, ObjectType, Option } from '../../../shared/types';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { createMappingWorkspaceModel } from '../lib/mappingWorkspaceModel';
import type { MappingWorkspaceSectionSummary } from '../lib/mappingWorkspacePageModel';
import { buildSectionSummaries, buildWorkspaceNavigatorSummary, buildWorkspaceSummary } from '../lib/mappingWorkspacePageModel';
import { buildMappingWorkspaceCompletionModel, type WorkflowSummaryCard } from '../lib/mappingWorkspaceCompletionModel';
import type { WorkflowGuideCard } from '../ui/components/WorkflowCompletionSummary';

interface UseWorkspaceDerivedModelsArgs {
  contextObjectTypes: ObjectType[];
  advancedCenterOfCoordinates: AdvancedSettings['centerOfCoordinates'];
  workflowSetupCompleted: boolean;
  selectedOption: Option;
  selectedSettingsType: SettingsType;
  selectedProfileKey: string;
}

/**
 * Pure view-model derivations for the mapping workspace: the workspace model,
 * scope/section summaries, completion model, and guide cards. Everything here
 * is a memoized function of the configuration stores + selection inputs — no
 * effects, no callbacks, no orchestration.
 */
export const useWorkspaceDerivedModels = ({
  contextObjectTypes,
  advancedCenterOfCoordinates,
  workflowSetupCompleted,
  selectedOption,
  selectedSettingsType,
  selectedProfileKey,
}: UseWorkspaceDerivedModelsArgs) => {
  const menuConfig = useConfigurationStore((state) => state.menuConfiguration);
  const connectorConfig = useConfigurationStore((state) => state.connectorBindingConfiguration);
  const processConfig = useConfigurationStore((state) => state.processPropertiesConfiguration);
  const boltConfig = useConfigurationStore((state) => state.boltConfiguration);
  const categoryConfig = useConfigurationStore((state) => state.additionalCategoriesConfiguration);

  const workspaceModel = useMemo(
    () =>
      createMappingWorkspaceModel({
        objectTypes: contextObjectTypes,
        menuConfig,
        connectorConfig,
        boltConfig,
        categoryConfig,
        processConfig,
      }),
    [boltConfig, categoryConfig, connectorConfig, contextObjectTypes, menuConfig, processConfig]
  );

  const allMappingRows = useMemo(
    () =>
      contextObjectTypes.flatMap((objectType) =>
        objectType.attributes.map((attribute) => ({
          objectTypeName: objectType.name,
          attribute,
          insight: workspaceModel.getRowInsight(objectType.name, attribute),
        }))
      ),
    [contextObjectTypes, workspaceModel]
  );

  const scopeSummaries = useMemo(
    () =>
      Object.fromEntries(
        contextObjectTypes.map((objectType) => [objectType.name, workspaceModel.getScopeSummary(objectType)])
      ),
    [contextObjectTypes, workspaceModel]
  );

  const workspaceSummary = useMemo(() => buildWorkspaceSummary(allMappingRows), [allMappingRows]);

  const workspaceSectionSummary = workspaceModel.getSectionSummary();
  const workspaceReady = workspaceSummary.requiredOpen === 0;
  const workspaceSummaryText = workspaceReady
    ? 'Ready'
    : `${workspaceSummary.requiredOpen} open`;

  const sectionSummaries = useMemo(
    () =>
      buildSectionSummaries({
        allScopeSummary: scopeSummaries.ALL,
        workspaceSectionSummary: workspaceSectionSummary as {
          processConditions: MappingWorkspaceSectionSummary;
          projectProperties: MappingWorkspaceSectionSummary;
        },
        centerOfCoordinates: advancedCenterOfCoordinates,
      }),
    [advancedCenterOfCoordinates, scopeSummaries.ALL, workspaceSectionSummary]
  );

  const workspaceNavigatorSummary = useMemo(
    () => buildWorkspaceNavigatorSummary(contextObjectTypes.length, workspaceSummary),
    [contextObjectTypes.length, workspaceSummary]
  );

  const completionModel = useMemo(
    () =>
      buildMappingWorkspaceCompletionModel({
        workflowSetupCompleted,
        selectedOption,
        selectedSettingsType,
        selectedProfileKey,
        scopeCount: contextObjectTypes.length,
        workspaceReady,
        workspaceSummary,
        connectorConfig,
        boltConfig,
        categoryConfig,
        processConfig,
      }),
    [
      boltConfig,
      categoryConfig,
      connectorConfig,
      contextObjectTypes.length,
      workflowSetupCompleted,
      processConfig,
      selectedOption,
      selectedProfileKey,
      selectedSettingsType,
      workspaceReady,
      workspaceSummary,
    ]
  );
  const showCompletedWorkflowChrome = completionModel.showCompletedWorkflowChrome;
  const showMappingWorkspaceSummary = showCompletedWorkflowChrome || selectedOption !== null;
  const workflowSummaryChips = completionModel.workflowSummaryChips;
  const workflowSummaryCards: WorkflowSummaryCard[] = completionModel.workflowSummaryCards;
  const workspaceGuides = useMemo<WorkflowGuideCard[]>(
    () =>
      completionModel.workspaceGuides.map((guide) => ({
        ...guide,
        icon:
          guide.iconId === 'mapping' ? (
            <SettingsIcon sx={{ fontSize: '0.95rem' }} />
          ) : guide.iconId === 'quickFill' ? (
            <QuickFillIcon sx={{ fontSize: '0.95rem' }} />
          ) : guide.iconId === 'validation' ? (
            <ValidationIcon sx={{ fontSize: '0.95rem' }} />
          ) : (
            <ExportIcon sx={{ fontSize: '0.95rem' }} />
          ),
      })),
    [completionModel.workspaceGuides]
  );

  const selectedProfileName = useMemo(() => {
    const [profileName] = selectedProfileKey.split('::');
    return profileName || 'Select profile';
  }, [selectedProfileKey]);

  return {
    workspaceModel,
    scopeSummaries,
    sectionSummaries,
    workspaceNavigatorSummary,
    workspaceSummary,
    workspaceReady,
    workspaceSummaryText,
    showCompletedWorkflowChrome,
    showMappingWorkspaceSummary,
    workflowSummaryChips,
    workflowSummaryCards,
    workspaceGuides,
    selectedProfileName,
  };
};
